import { isNil } from 'lodash';
import 'reflect-metadata';

import { logger } from '@assess/shared/logger';

import { Constructor } from './constructor.type';
import { IDynamicComponentMetadata } from '../types/dynamic-component-metadata.interface';
import { DynamicMetadataType } from '../types/dynamic-metadata-type.enum';
import { IDynamicPropertyMetadata } from '../types/dynamic-property-metadata.interface';

type PropertyMetadata = { metadata: IDynamicPropertyMetadata } & { tag: string };

export interface IDynamicComponentMixin {
  applyProps: (props?: object) => void;
}

export type DynamicComponentCtor = Constructor<IDynamicComponentMixin>;

export function mixinDynamicComponent<T extends Constructor<{}>>(base: T): DynamicComponentCtor & T {
  class DynamicForm extends base implements IDynamicComponentMixin {
    private static _componentMetadata: IDynamicComponentMetadata;

    private readonly instanceLogger = logger.child({ context: this.constructor.name });
    private _propertyIds!: string[];
    private readonly _propertiesMetadata!: PropertyMetadata[];
    private readonly propertyIdRegex = /^[a-z0-9]+$/i;

    private get propertyIds() {
      if (!this._propertyIds) {
        this._propertyIds = Reflect.getMetadata(DynamicMetadataType.Properties, this) ?? [];
      }

      return this._propertyIds;
    }

    private get propertiesMetadata() {
      return this._propertiesMetadata;
    }

    constructor(...args: any[]) {
      super(...args);

      this._propertiesMetadata = this.buildPropertiesMetadata();
    }

    static componentMetadata() {
      if (!this._componentMetadata) {
        this._componentMetadata = Reflect.getMetadata(DynamicMetadataType.Component, this);
      }

      return this._componentMetadata;
    }

    applyProps(props?: Record<string, any>) {
      if (!props) {
        this.instanceLogger.warn('No properties to apply');

        return;
      }

      if (!this.propertyIds.length) {
        this.instanceLogger.warn('No property metadata defined');

        return;
      }

      Object.keys(props)
        .filter((tag) => !this.propertyIds.includes(tag))
        .forEach((tag) => this.instanceLogger.warn({ data: { tag } }, 'Unknown prop'));

      this.propertiesMetadata.forEach((meta) => {
        const prop = props[meta.tag];

        if (isNil(prop)) {
          return;
        }

        if (meta.metadata.validator && !meta.metadata.validator(props[meta.tag])) {
          this.instanceLogger.error({ data: { tag: meta.tag, metadata: meta.metadata } }, 'Prop is invalid; skipping');

          return;
        }

        (this as Record<string, any>)[meta.tag] = prop;
      });
    }

    private buildPropertiesMetadata(): PropertyMetadata[] {
      return this.propertyIds
        .map((key) => this.buildPropertyMetadata(key))
        .filter((meta) => !!meta) as PropertyMetadata[];
    }

    private buildPropertyMetadata(tag: string): PropertyMetadata | undefined {
      if (!this.propertyIdRegex.test(tag)) {
        this.instanceLogger.warn({ data: { tag, regex: this.propertyIdRegex.toString() } }, 'Tag invalid');

        return undefined;
      }

      const metadata = Reflect.getMetadata(DynamicMetadataType.Property, this, tag) ?? {};

      return { tag, metadata };
    }
  }

  return DynamicForm;
}
